import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@availity/element';
import getSubscriptionDetails, { SubscriptionData } from '../../../api/subscriptionApi';
import { getUserDetailsById } from '../../../api/userApi';
import { typography } from '../../../static/constants/typography';
import { themeColors, iconFilters } from '../../../static/constants/styles';
import { useSessionStore, useAppStore } from '../../../hooks';
import arrowSort from '../../../static/assets/icons/arrow-sort.svg';
import noSubscriptionIcon from '../../../static/assets/icons/no-subscription.svg';
import { colors } from '../../../static/constants/theme';
import { trackEvent } from '../../Utils/splunkEventHelper';

interface AccessHubTabProps {
  theme: 'light' | 'dark';
}

const DATE_FORMAT: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: '2-digit' };
const HEADERS = ['Organization', 'Plans', 'Effective Date'] as const;
const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const TableCell = ({ cellText, children }: { cellText: string; children: React.ReactNode }) => (
  <Box
    sx={{
      p: '10px 16px',
      ...typography.latoRegular16Alt,
      color: cellText,
      borderRight: `1px solid ${colors.border.outline}`,
    }}
  >
    {children}
  </Box>
);

const AccessHubTab = ({ theme }: AccessHubTabProps) => {
  const themeStyle = themeColors[theme];
  const userId = useSessionStore(
    (store: unknown) => ((store as { user?: { id?: string } })?.user?.id) ?? ''
  );
  const { setOrganizations } = useAppStore((store) => store);
  const organizations = useAppStore((store) => store.organizations);
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData[]>([]);
  const [sort, setSort] = useState<{ column: (typeof HEADERS)[number]; asc: boolean }>({
    column: 'Effective Date',
    asc: false,
  });

  useEffect(() => {
    if (!userId) return;
    const fetch = async () => {
      try {
        const userDetails = await getUserDetailsById(userId);
        const orgs = userDetails?.organizations ?? [];
        setOrganizations(orgs);
        const orgIds = orgs.map((org: { customerId: string }) => org.customerId);
        const data = await getSubscriptionDetails(orgIds);
        setSubscriptionData(data);
      } catch (error) {
        trackEvent({
          event: 'subscription-fetch-failed',
          status: (error as { response?: { status?: number } })?.response?.status,
          api: 'subscription-details-api',
        });
      }
    };
    fetch();
  }, [userId, setOrganizations]);

  const mappedSubscriptions = useMemo(
    () =>
      subscriptionData.map((item) => ({
        id: item.key_id,
        organization: organizations.find((org) => org.customerId === item.org_id)?.name ?? item.org_name,
        plan: item.key_type ? capitalize(item.key_type) : '',
        expirationMs: new Date(item.expiration_time).getTime(),
        effectiveDate: new Date(item.expiration_time).toLocaleDateString('en-US', DATE_FORMAT),
      })),
    [subscriptionData, organizations]
  );

  const sortedSubscriptions = useMemo(() => {
    const { column, asc } = sort;
    return [...mappedSubscriptions].sort((a, b) => {
      let cmp = 0;
      switch (column) {
        case 'Effective Date':
          cmp = a.expirationMs - b.expirationMs;
          break;
        case 'Organization':
          cmp = a.organization.localeCompare(b.organization);
          break;
        case 'Plans':
          cmp = a.plan.localeCompare(b.plan);
          break;
        default:
          break;
      }
      return asc ? cmp : -cmp;
    });
  }, [mappedSubscriptions, sort]);

  return (
    <Box sx={{ p: '64px 155px', bgcolor: themeStyle.background, minHeight: 'calc(100vh - 120px)' }}>
      <Box sx={{ mb: '39px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Box component="h1" sx={{ ...typography.latoSemibold28, letterSpacing: 0, color: themeStyle.text, m: 0 }}>
          My Subscriptions
        </Box>
        <Box sx={{ ...typography.latoRegular16Alt, letterSpacing: 0, color: themeStyle.text, m: 0 }}>
          API Subscription Information
        </Box>
      </Box>

      {sortedSubscriptions.length === 0 ? (
        <Box sx={{
          border: `1px solid ${colors.border.outline}`,
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '533px',
          gap: '16px',
        }}>
      
            <img src={noSubscriptionIcon} alt="no subscriptions" width="100" height="100" />
         
          <Box sx={{ ...typography.montSemibold20, color: themeStyle.text }}>
            You have no subscriptions at this time.
          </Box>
        </Box>
      ) : (
        <Box sx={{ border: `1px solid ${colors.border.outline}`, borderRadius: '8px', overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              bgcolor: themeStyle.cardBackground,
              borderBottom: `1px solid ${colors.border.outline}`,
            }}
          >
            {HEADERS.map((header) => (
              <Box
                key={header}
                sx={{
                  p: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  ...typography.latoRegular16,
                  letterSpacing: 0,
                  color: themeStyle.text,
                  borderRight: `1px solid ${colors.border.outline}`,
                }}
              >
                {header}
                <Box
                  component="span"
                  onClick={() => setSort((p) => ({ column: header, asc: p.column === header ? !p.asc : false }))}
                  sx={{ cursor: 'pointer', display: 'flex' }}
                >
                  <img
                    data-testid={`sort-icon-${header.toLowerCase().replace(' ', '-')}`}
                    src={arrowSort}
                    alt="sort"
                    width="18"
                    height="18"
                    style={{
                      filter: theme === 'light' ? iconFilters.dark : iconFilters.light,
                      opacity: sort.column === header ? 1 : 0.4,
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
          {sortedSubscriptions.map((row) => (
            <Box
              key={row.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                bgcolor: themeStyle.background,
                '&:not(:last-child)': { borderBottom: `1px solid ${colors.border.outline}` },
              }}
            >
              <TableCell cellText={themeStyle.cellText}>{row.organization}</TableCell>
              <TableCell cellText={themeStyle.cellText}>{row.plan}</TableCell>
              <TableCell cellText={themeStyle.cellText}>{row.effectiveDate}</TableCell>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default AccessHubTab;
